import { useState, useEffect } from "react";
import { fetchAndyNotes } from "../../api/notesApi";
import { formatTimeStamp } from "../../api/callData";
import { Icon } from "@iconify/react/dist/iconify.js";

// Helper function to highlight search text
const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
        regex.test(part) ? (
            <span key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
                {part}
            </span>
        ) : part
    );
};

const AndyNotesTable = ({ notes, selectedRows, setSelectedRows, searchText }) => {
    const selectAll = (e) => {
        if (e.target.checked) {
            setSelectedRows(notes.map((note, index) => index));
        } else {
            setSelectedRows([]);
        }
    };

    const selectRow = (index) => {
        setSelectedRows((prev) => {
            return prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index];
        });
    };

    return (
        <div className="w-full h-full rounded-md overflow-hidden flex flex-col">
            {/* Desktop Header (xl and above) */}
            <div className="flex-shrink-0 hidden sm:block">
                <div className="flex text-font-primary py-3 px-0 card-container items-center text-sm font-semibold">
                    <div className="pl-4 w-[140px] max-w-[140px]">Caller Info</div>
                    <div className="pl-2 flex-1">Note</div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollable">
                {/* Desktop List View (xl and above) */}
                <div className="hidden sm:block">
                    {notes.map((note, index) => {
                        return (
                            <div
                                key={index}
                                className="flex mb-2 text-font-secondary hover:text-font-primary hover:bg-background hover:button-container hover:border-none cursor-pointer transition-colors ease-out-in duration-300 text-sm"
                            >
                                <div className="flex flex-col gap-1 pt-2 pl-4 w-[140px] max-w-[140px] text-font-primary flex-shrink-0">
                                    <span className="font-semibold text-primary text-sm">{highlightText(note.caller_id, searchText)}</span>
                                    <span className="text-sm text-font-secondary">{highlightText(formatTimeStamp(note.timestamp), searchText)}</span>
                                </div>
                                <div className="p-2 pr-4 flex-1 min-w-0">
                                    <div className="text-sm leading-relaxed">
                                        {highlightText(note.note_text, searchText)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Mobile Card View (below xl) */}
                <div className="sm:hidden p-2">
                    {notes.map((note, index) => {
                        return (
                            <div
                                key={index}
                                className="p-3 rounded-md cursor-pointer transition-all duration-300 ease-out-in hover:shadow-button group hover:scale-[1.01] card-container bg-background border border-bgAccent min-h-[60px] flex flex-col mb-2"
                            >
                                <div className="flex items-start justify-between mb-2 flex-shrink-0">
                                    <div className="flex items-center gap-2">
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-primary text-base">
                                                {highlightText(note.caller_id, searchText)}
                                            </span>
                                            <span className="text-xs text-font-secondary">
                                                {highlightText(formatTimeStamp(note.timestamp), searchText)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-2">
                                    <p className="text-sm text-font-secondary leading-relaxed group-hover:text-font-primary">
                                        {highlightText(note.note_text, searchText)}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {notes.length === 0 && (
                    <div className="text-center py-10 text-gray-400">No notes found.</div>
                )}
            </div>


        </div>
    );
};

const AndyNotesPage = () => {
    const [notes, setNotes] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        notesTable: false
    });

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, notesTable: true }));
        }, 300));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    useEffect(() => {
        const loadNotes = async () => {
            try {
                setLoading(true);
                const response = await fetchAndyNotes();
                setNotes(response.notes || []);
                setError(null);
            } catch (err) {
                setError('Failed to load notes');
                console.error('Error loading notes:', err);
            } finally {
                setLoading(false);
            }
        };

        loadNotes();
    }, []);

    // Filter notes based on search text
    const filteredNotes = notes.filter(note => {
        if (searchText === '') return true;
        
        const searchLower = searchText.toLowerCase();
        const searchableFields = [
            note.caller_id,
            note.note_text,
            note.timestamp
        ].filter(Boolean); // Remove undefined/null values
        
        return searchableFields.some(field => 
            field.toString().toLowerCase().includes(searchLower)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-font-secondary">Loading notes...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="responsive-page-layout">
            <div className={`flex flex-col lg:flex-row justify-between lg:items-center items-start w-full gap-4 lg:gap-0 transition-all duration-700 ease-out ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                <div className="flex flex-col gap-2 items-start">
                    <h1 className="header-textbox">
                        <Icon icon="material-symbols:note-alt-rounded" className="header-icon"></Icon>
                        Andy Notes
                    </h1>
                    <p className="text-font-secondary text-sm sm:text-base">
                        View and manage call notes from Andy ({filteredNotes.length} of {notes.length} notes)
                    </p>
                </div>
                <div className="flex flex-row gap-2 w-full lg:w-auto">
                    <div className="relative w-full sm:w-auto">
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Search notes..."
                            className="p-2 sm:p-3 button-container rounded-md text-xs sm:text-sm w-full sm:w-auto pl-3"
                        />
                    </div>
                </div>
            </div>
            
            <div className={`flex-1 min-h-0 transition-all duration-700 ease-out ${
                animationStates.notesTable
                    ? 'opacity-100 translate-y-0 scale-100'
                    : 'opacity-0 translate-y-4 scale-95'
            }`}>
                <AndyNotesTable 
                    notes={filteredNotes}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    searchText={searchText}
                />
            </div>
        </div>
    );
};

export default AndyNotesPage; 